/**
 * Map Drawing Engine — handles click/drag/mousemove interactions on the
 * MapLibre GL JS map to create GeoJSON drawing features.
 *
 * Renders all drawings via a single GeoJSON source + multiple style layers.
 * Each feature has `properties.drawingId`, `properties.drawingType`, and
 * optional `properties.label` / `properties.color`.
 */

import type { Map as MaplibreMap, MapMouseEvent, GeoJSONSource, LngLat } from 'maplibre-gl';
import type { Feature, FeatureCollection, Point, LineString, Polygon } from 'geojson';

export type DrawingType = 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehand' | 'arrow' | 'text' | 'measurement';

export interface DrawingProperties {
  drawingId: string;
  drawingType: DrawingType;
  label: string;
  color: string;
  radiusNm?: number;
  measurements?: string;
  [key: string]: unknown;
}

export interface DrawingFeatureRecord {
  id: string;
  type: DrawingType;
  label: string;
  color: string;
  feature: Feature;
}

export type DrawingEngineCallback = (features: DrawingFeatureRecord[]) => void;

const SOURCE_ID = 'drawing-source';
const VERTEX_SOURCE_ID = 'drawing-vertex-source';
const PREVIEW_SOURCE_ID = 'drawing-preview-source';

const DEFAULT_COLOR = '#F59E0B';
const DEG_PER_NM = 1 / 60;
const NM_PER_RADIAN = 3440.065;

let idCounter = 0;
function nextId(): string { return `draw-${Date.now()}-${idCounter++}`; }

// ─── Geometry helpers ────────────────────────────────────────────────────────

function haversineNm(a: LngLat, b: LngLat): number {
  const toRad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toRad;
  const dLng = (b.lng - a.lng) * toRad;
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) * sinLng * sinLng;
  return 2 * Math.asin(Math.sqrt(h)) * NM_PER_RADIAN;
}

function bearing(a: LngLat, b: LngLat): number {
  const toRad = Math.PI / 180;
  const dLng = (b.lng - a.lng) * toRad;
  const x = Math.sin(dLng) * Math.cos(b.lat * toRad);
  const y = Math.cos(a.lat * toRad) * Math.sin(b.lat * toRad) - Math.sin(a.lat * toRad) * Math.cos(b.lat * toRad) * Math.cos(dLng);
  return ((Math.atan2(x, y) * 180 / Math.PI) + 360) % 360;
}

function makeCirclePolygon(center: [number, number], radiusNm: number, segments = 64): [number, number][] {
  const coords: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (2 * Math.PI * i) / segments;
    const dLat = radiusNm * DEG_PER_NM * Math.cos(angle);
    const dLng = (radiusNm * DEG_PER_NM * Math.sin(angle)) / Math.cos((center[1] * Math.PI) / 180);
    coords.push([center[0] + dLng, center[1] + dLat]);
  }
  return coords;
}

function makeRectPolygon(a: [number, number], b: [number, number]): [number, number][] {
  return [[a[0], a[1]], [b[0], a[1]], [b[0], b[1]], [a[0], b[1]], [a[0], a[1]]];
}

function emptyFc(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

// ─── Drawing Engine ──────────────────────────────────────────────────────────

export class DrawingEngine {
  private map: MaplibreMap;
  private features: DrawingFeatureRecord[] = [];
  private onChange: DrawingEngineCallback;
  private activeTool: DrawingType | null = null;
  private currentColor = DEFAULT_COLOR;
  private pendingCoords: [number, number][] = [];
  private isDrawing = false;
  private isFreehandDragging = false;
  private boundHandlers: Record<string, (e: MapMouseEvent) => void> = {};
  private initialized = false;

  constructor(map: MaplibreMap, onChange: DrawingEngineCallback) {
    this.map = map;
    this.onChange = onChange;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /** Initialize sources and layers on the map — call once after map load */
  init(): void {
    if (this.initialized) return;
    const m = this.map;

    // Main drawing features source
    if (!m.getSource(SOURCE_ID)) {
      m.addSource(SOURCE_ID, { type: 'geojson', data: emptyFc() });
      m.addLayer({ id: 'drawing-fill', type: 'fill', source: SOURCE_ID, filter: ['==', '$type', 'Polygon'], paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.2 } });
      m.addLayer({ id: 'drawing-line', type: 'line', source: SOURCE_ID, filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']], paint: { 'line-color': ['get', 'color'], 'line-width': 2.5 } });
      m.addLayer({ id: 'drawing-point', type: 'circle', source: SOURCE_ID, filter: ['==', '$type', 'Point'], paint: { 'circle-radius': 6, 'circle-color': ['get', 'color'], 'circle-stroke-width': 2, 'circle-stroke-color': '#0F172A' } });
      m.addLayer({ id: 'drawing-label', type: 'symbol', source: SOURCE_ID, layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-offset': [0, 1.5], 'text-anchor': 'top', 'text-font': ['Open Sans Regular'] }, paint: { 'text-color': '#F1F5F9', 'text-halo-color': '#0F172A', 'text-halo-width': 1 } });
    }

    // Preview source (shows in-progress geometry while drawing)
    if (!m.getSource(PREVIEW_SOURCE_ID)) {
      m.addSource(PREVIEW_SOURCE_ID, { type: 'geojson', data: emptyFc() });
      m.addLayer({ id: 'preview-fill', type: 'fill', source: PREVIEW_SOURCE_ID, filter: ['==', '$type', 'Polygon'], paint: { 'fill-color': DEFAULT_COLOR, 'fill-opacity': 0.15 } });
      m.addLayer({ id: 'preview-line', type: 'line', source: PREVIEW_SOURCE_ID, filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']], paint: { 'line-color': DEFAULT_COLOR, 'line-width': 2, 'line-dasharray': [4, 3] } });
      m.addLayer({ id: 'preview-point', type: 'circle', source: PREVIEW_SOURCE_ID, filter: ['==', '$type', 'Point'], paint: { 'circle-radius': 5, 'circle-color': DEFAULT_COLOR, 'circle-stroke-width': 1, 'circle-stroke-color': '#0F172A' } });
    }

    // Vertex source (shows clickable vertices for editing)
    if (!m.getSource(VERTEX_SOURCE_ID)) {
      m.addSource(VERTEX_SOURCE_ID, { type: 'geojson', data: emptyFc() });
      m.addLayer({ id: 'drawing-vertex', type: 'circle', source: VERTEX_SOURCE_ID, paint: { 'circle-radius': 4, 'circle-color': '#fff', 'circle-stroke-width': 2, 'circle-stroke-color': DEFAULT_COLOR } });
    }

    this.initialized = true;
  }

  /** Remove all sources/layers and event handlers from the map */
  destroy(): void {
    this.deactivate();
    const m = this.map;
    for (const layerId of ['drawing-fill', 'drawing-line', 'drawing-point', 'drawing-label', 'preview-fill', 'preview-line', 'preview-point', 'drawing-vertex']) {
      try { m.removeLayer(layerId); } catch { /* ok */ }
    }
    for (const srcId of [SOURCE_ID, PREVIEW_SOURCE_ID, VERTEX_SOURCE_ID]) {
      try { m.removeSource(srcId); } catch { /* ok */ }
    }
    this.initialized = false;
  }

  // ─── Tool activation ─────────────────────────────────────────────────────

  /** Activate a drawing tool — starts listening for map events */
  activate(tool: DrawingType, color?: string): void {
    this.deactivate();
    this.init();
    this.activeTool = tool;
    this.currentColor = color ?? DEFAULT_COLOR;
    this.pendingCoords = [];
    this.isDrawing = false;
    this.isFreehandDragging = false;
    this.map.getCanvas().style.cursor = 'crosshair';

    const onClick = (e: MapMouseEvent) => this.handleClick(e);
    const onMouseMove = (e: MapMouseEvent) => this.handleMouseMove(e);
    const onDblClick = (e: MapMouseEvent) => this.handleDblClick(e);
    const onMouseDown = (e: MapMouseEvent) => this.handleMouseDown(e);
    const onMouseUp = (e: MapMouseEvent) => this.handleMouseUp(e);

    this.boundHandlers = { click: onClick, mousemove: onMouseMove, dblclick: onDblClick, mousedown: onMouseDown, mouseup: onMouseUp };
    this.map.on('click', onClick);
    this.map.on('mousemove', onMouseMove);
    this.map.on('dblclick', onDblClick);
    if (tool === 'freehand') {
      this.map.on('mousedown', onMouseDown);
      this.map.on('mouseup', onMouseUp);
    }
  }

  /** Deactivate the current tool — stop listening for events */
  deactivate(): void {
    this.activeTool = null;
    this.pendingCoords = [];
    this.isDrawing = false;
    this.isFreehandDragging = false;
    this.map.getCanvas().style.cursor = '';
    this.clearPreview();

    for (const [event, handler] of Object.entries(this.boundHandlers)) {
      this.map.off(event as any, handler);
    }
    this.boundHandlers = {};
  }

  setColor(color: string): void { this.currentColor = color; }
  getActiveTool(): DrawingType | null { return this.activeTool; }
  getFeatures(): DrawingFeatureRecord[] { return [...this.features]; }

  // ─── Feature management ──────────────────────────────────────────────────

  removeFeature(id: string): void {
    this.features = this.features.filter((f) => f.id !== id);
    this.syncToMap();
    this.onChange(this.getFeatures());
  }

  clearAll(): void {
    this.features = [];
    this.syncToMap();
    this.onChange(this.getFeatures());
  }

  setFeatureVisibility(id: string, visible: boolean): void {
    const f = this.features.find((f) => f.id === id);
    if (f) { f.feature.properties!.visible = visible; this.syncToMap(); }
  }

  exportGeoJSON(): FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: this.features.map((f) => f.feature),
    };
  }

  importGeoJSON(fc: FeatureCollection): void {
    for (const f of fc.features) {
      const id = nextId();
      const props = f.properties ?? {};
      this.features.push({
        id,
        type: (props.drawingType as DrawingType) ?? 'point',
        label: props.label ?? id,
        color: props.color ?? DEFAULT_COLOR,
        feature: { ...f, properties: { ...props, drawingId: id, color: props.color ?? DEFAULT_COLOR } },
      });
    }
    this.syncToMap();
    this.onChange(this.getFeatures());
  }

  // ─── Event handlers ──────────────────────────────────────────────────────

  private handleClick(e: MapMouseEvent): void {
    if (!this.activeTool) return;
    const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    switch (this.activeTool) {
      case 'point':
        this.commitFeature(this.makePointFeature(coord));
        break;

      case 'text': {
        const label = prompt('Enter label text:');
        if (label) this.commitFeature(this.makeTextFeature(coord, label));
        break;
      }

      case 'line':
      case 'measurement':
      case 'arrow':
        this.pendingCoords.push(coord);
        this.isDrawing = true;
        this.updateVertices();
        break;

      case 'polygon':
        this.pendingCoords.push(coord);
        this.isDrawing = true;
        this.updateVertices();
        break;

      case 'circle':
        if (this.pendingCoords.length === 0) {
          this.pendingCoords.push(coord);
          this.isDrawing = true;
        } else {
          const center = this.pendingCoords[0];
          const radiusNm = haversineNm(
            { lng: center[0], lat: center[1] } as LngLat,
            e.lngLat,
          );
          this.commitFeature(this.makeCircleFeature(center, radiusNm));
          this.pendingCoords = [];
          this.isDrawing = false;
        }
        break;

      case 'rectangle':
        if (this.pendingCoords.length === 0) {
          this.pendingCoords.push(coord);
          this.isDrawing = true;
        } else {
          this.commitFeature(this.makeRectangleFeature(this.pendingCoords[0], coord));
          this.pendingCoords = [];
          this.isDrawing = false;
        }
        break;
    }
  }

  private handleDblClick(e: MapMouseEvent): void {
    if (!this.activeTool || !this.isDrawing) return;
    e.preventDefault();

    if (this.activeTool === 'line' || this.activeTool === 'measurement' || this.activeTool === 'arrow') {
      if (this.pendingCoords.length >= 2) {
        if (this.activeTool === 'measurement') {
          this.commitFeature(this.makeMeasurementFeature([...this.pendingCoords]));
        } else if (this.activeTool === 'arrow') {
          this.commitFeature(this.makeArrowFeature([...this.pendingCoords]));
        } else {
          this.commitFeature(this.makeLineFeature([...this.pendingCoords]));
        }
      }
      this.pendingCoords = [];
      this.isDrawing = false;
      this.clearPreview();
      this.clearVertices();
    } else if (this.activeTool === 'polygon') {
      if (this.pendingCoords.length >= 3) {
        const ring = [...this.pendingCoords, this.pendingCoords[0]];
        this.commitFeature(this.makePolygonFeature(ring));
      }
      this.pendingCoords = [];
      this.isDrawing = false;
      this.clearPreview();
      this.clearVertices();
    }
  }

  private handleMouseMove(e: MapMouseEvent): void {
    if (!this.activeTool || !this.isDrawing) return;
    const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    if (this.activeTool === 'freehand' && this.isFreehandDragging) {
      this.pendingCoords.push(coord);
      this.showPreviewLine([...this.pendingCoords]);
      return;
    }

    if (this.pendingCoords.length === 0) return;

    switch (this.activeTool) {
      case 'line':
      case 'measurement':
      case 'arrow':
        this.showPreviewLine([...this.pendingCoords, coord]);
        break;
      case 'polygon':
        if (this.pendingCoords.length >= 2) {
          this.showPreviewPolygon([...this.pendingCoords, coord, this.pendingCoords[0]]);
        } else {
          this.showPreviewLine([...this.pendingCoords, coord]);
        }
        break;
      case 'circle': {
        const center = this.pendingCoords[0];
        const r = haversineNm({ lng: center[0], lat: center[1] } as LngLat, e.lngLat);
        this.showPreviewPolygon(makeCirclePolygon(center, r));
        break;
      }
      case 'rectangle':
        this.showPreviewPolygon(makeRectPolygon(this.pendingCoords[0], coord));
        break;
    }
  }

  private handleMouseDown(e: MapMouseEvent): void {
    if (this.activeTool !== 'freehand') return;
    this.isFreehandDragging = true;
    this.isDrawing = true;
    this.pendingCoords = [[e.lngLat.lng, e.lngLat.lat]];
    this.map.dragPan.disable();
  }

  private handleMouseUp(_e: MapMouseEvent): void {
    if (this.activeTool !== 'freehand' || !this.isFreehandDragging) return;
    this.isFreehandDragging = false;
    this.map.dragPan.enable();
    if (this.pendingCoords.length >= 2) {
      this.commitFeature(this.makeLineFeature([...this.pendingCoords]));
    }
    this.pendingCoords = [];
    this.isDrawing = false;
    this.clearPreview();
  }

  // ─── Feature factories ───────────────────────────────────────────────────

  private makeProps(type: DrawingType, label: string, extra?: Record<string, unknown>): DrawingProperties {
    return { drawingId: '', drawingType: type, label, color: this.currentColor, ...extra };
  }

  private makePointFeature(coord: [number, number]): Feature<Point> {
    return { type: 'Feature', properties: this.makeProps('point', `Point ${this.features.length + 1}`), geometry: { type: 'Point', coordinates: coord } };
  }

  private makeTextFeature(coord: [number, number], text: string): Feature<Point> {
    return { type: 'Feature', properties: this.makeProps('text', text), geometry: { type: 'Point', coordinates: coord } };
  }

  private makeLineFeature(coords: [number, number][]): Feature<LineString> {
    const dist = this.totalDistance(coords);
    return { type: 'Feature', properties: this.makeProps('line', `Line ${this.features.length + 1}`, { measurements: `${dist.toFixed(1)} NM` }), geometry: { type: 'LineString', coordinates: coords } };
  }

  private makeArrowFeature(coords: [number, number][]): Feature<LineString> {
    const dist = this.totalDistance(coords);
    const brg = coords.length >= 2 ? bearing({ lng: coords[coords.length - 2][0], lat: coords[coords.length - 2][1] } as LngLat, { lng: coords[coords.length - 1][0], lat: coords[coords.length - 1][1] } as LngLat) : 0;
    return { type: 'Feature', properties: this.makeProps('arrow', `Arrow ${this.features.length + 1}`, { measurements: `${dist.toFixed(1)} NM, ${brg.toFixed(0)}°T`, bearing: brg }), geometry: { type: 'LineString', coordinates: coords } };
  }

  private makeMeasurementFeature(coords: [number, number][]): Feature<LineString> {
    const legs: string[] = [];
    let total = 0;
    for (let i = 1; i < coords.length; i++) {
      const d = haversineNm({ lng: coords[i - 1][0], lat: coords[i - 1][1] } as LngLat, { lng: coords[i][0], lat: coords[i][1] } as LngLat);
      const b = bearing({ lng: coords[i - 1][0], lat: coords[i - 1][1] } as LngLat, { lng: coords[i][0], lat: coords[i][1] } as LngLat);
      total += d;
      legs.push(`${d.toFixed(1)} NM / ${b.toFixed(0)}°`);
    }
    return { type: 'Feature', properties: this.makeProps('measurement', `${total.toFixed(1)} NM total`, { measurements: legs.join(' → '), totalNm: total, legCount: coords.length - 1 }), geometry: { type: 'LineString', coordinates: coords } };
  }

  private makePolygonFeature(ring: [number, number][]): Feature<Polygon> {
    return { type: 'Feature', properties: this.makeProps('polygon', `Polygon ${this.features.length + 1}`), geometry: { type: 'Polygon', coordinates: [ring] } };
  }

  private makeCircleFeature(center: [number, number], radiusNm: number): Feature<Polygon> {
    const ring = makeCirclePolygon(center, radiusNm);
    return { type: 'Feature', properties: this.makeProps('circle', `Circle ${this.features.length + 1}`, { radiusNm, measurements: `R=${radiusNm.toFixed(1)} NM` }), geometry: { type: 'Polygon', coordinates: [ring] } };
  }

  private makeRectangleFeature(a: [number, number], b: [number, number]): Feature<Polygon> {
    const ring = makeRectPolygon(a, b);
    return { type: 'Feature', properties: this.makeProps('rectangle', `Rect ${this.features.length + 1}`), geometry: { type: 'Polygon', coordinates: [ring] } };
  }

  private totalDistance(coords: [number, number][]): number {
    let d = 0;
    for (let i = 1; i < coords.length; i++) {
      d += haversineNm({ lng: coords[i - 1][0], lat: coords[i - 1][1] } as LngLat, { lng: coords[i][0], lat: coords[i][1] } as LngLat);
    }
    return d;
  }

  // ─── Map sync ────────────────────────────────────────────────────────────

  private commitFeature(feature: Feature): void {
    const id = nextId();
    const props = feature.properties as DrawingProperties;
    props.drawingId = id;
    this.features.push({
      id,
      type: props.drawingType,
      label: props.label,
      color: props.color,
      feature,
    });
    this.syncToMap();
    this.onChange(this.getFeatures());
  }

  private syncToMap(): void {
    const src = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: this.features.map((f) => f.feature),
    });
  }

  private showPreviewLine(coords: [number, number][]): void {
    const src = this.map.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } }],
    });
  }

  private showPreviewPolygon(ring: [number, number][]): void {
    const src = this.map.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }],
    });
  }

  private clearPreview(): void {
    const src = this.map.getSource(PREVIEW_SOURCE_ID) as GeoJSONSource | undefined;
    if (src) src.setData(emptyFc());
  }

  private updateVertices(): void {
    const src = this.map.getSource(VERTEX_SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: this.pendingCoords.map((c) => ({
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'Point' as const, coordinates: c },
      })),
    });
  }

  private clearVertices(): void {
    const src = this.map.getSource(VERTEX_SOURCE_ID) as GeoJSONSource | undefined;
    if (src) src.setData(emptyFc());
  }
} // end DrawingEngine
